import { useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n/i18n';
import {
  ITEM_STATUS_LABEL,
  MEASURE_LABEL,
  PROMPT_LEVELS,
  SESSION_STATUS_LABEL,
  itemLabel,
  saveResult,
  updateSession,
  type Goal,
  type Item,
  type ItemStatus,
  type Measure,
  type Result,
  type Session,
  type SessionStatus,
} from './api';
import Comments from './Comments';
import Evidence from './Evidence';
import { shortTime, useDates } from './dates';
import PlannerDialog from './PlannerDialog';
import { activityIndex, sessionToInput } from './SessionEditor';
import { MoveForm, STATUS_ICON, StatusWord, sessionName, sessionRepeat, useRepeatText } from './SessionParts';

type Props = {
  learnerId: string;
  session: Session;
  goals: Goal[];
  onClose: () => void;
  onChanged: () => Promise<void> | void;
  onEdit: (s: Session) => void;
  onMove: (s: Session, day: string) => Promise<void> | void;
};

const n = (s: string) => (s.trim() === '' || Number.isNaN(Number(s)) ? undefined : Math.max(0, Number(s)));
const v = (x: number | undefined) => (x == null ? '' : String(x));

export default function SessionView({ learnerId, session, goals, onClose, onChanged, onEdit, onMove }: Props) {
  const t = useT();
  const id = useId();
  const d = useDates();
  const repeatText = useRepeatText();
  const activities = useMemo(() => activityIndex(goals), [goals]);
  const [status, setStatus] = useState<SessionStatus>(session.status);
  const [note, setNote] = useState(session.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const time = shortTime(session.startTime);
  const repeat = sessionRepeat(session, repeatText);

  async function saveSession() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await updateSession(learnerId, session.id, { ...sessionToInput(session), status, note: note.trim() || null }, 'ONE');
      await onChanged();
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PlannerDialog title={sessionName(session, t('Session'))} onClose={onClose} className="planner-session-dialog">
      <div className="planner-view-head">
        <p className="planner-when">
          {d.long(session.day)}
          {time && <> · {time}</>}
          {session.minutes != null && <> · {t('{n} minutes', { n: session.minutes })}</>}
        </p>
        {session.who && <p className="planner-meta">{t('Run by {who}', { who: session.who })}</p>}
        {repeat && <p className="planner-meta">{repeat}</p>}
        <StatusWord status={session.status} />
        <div className="toolbar">
          <button type="button" className="btn secondary" onClick={() => onEdit(session)}>{t('Change the plan')}</button>
          <MoveForm session={session} onMove={onMove} />
        </div>
      </div>

      <h3>{t('What to do, in order')}</h3>
      {session.items.length === 0 && <p className="muted">{t('Nothing added to this session.')}</p>}
      <ol className="planner-view-items">
        {session.items.map((i) => {
          const a = i.activityId ? activities.get(i.activityId) : undefined;
          return (
            <li key={i.id} className="planner-view-item">
              <ItemPanel
                learnerId={learnerId}
                item={i}
                measure={a?.activity.measure ?? null}
                programId={i.programId ?? a?.activity.programId ?? null}
                instructions={a?.activity.instructions ?? null}
                onSaved={onChanged}
              />
            </li>
          );
        })}
      </ol>

      <section className="planner-view-outcome" aria-labelledby={`${id}-how`}>
        <h3 id={`${id}-how`}>{t('How the session went')}</h3>
        <fieldset className="planner-fieldset">
          <legend>{t('Session status')}</legend>
          <div className="planner-radios">
            {(Object.keys(SESSION_STATUS_LABEL) as SessionStatus[]).map((s) => (
              <label key={s}>
                <input type="radio" name={`${id}-status`} checked={status === s} onChange={() => { setStatus(s); setSaved(false); }} />
                <span aria-hidden="true">{STATUS_ICON[s]}</span> {t(SESSION_STATUS_LABEL[s])}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="field">
          <label htmlFor={`${id}-note`}>{t('Note')}</label>
          <textarea id={`${id}-note`} rows={4} maxLength={4000} value={note} onChange={(e) => { setNote(e.target.value); setSaved(false); }} aria-describedby={`${id}-note-hint`} />
          <span className="field-hint" id={`${id}-note-hint`}>{t('What happened, what was measured, what it means, what next')}</span>
        </div>
        {error && <div className="error" role="alert">{error}</div>}
        <div className="toolbar">
          <button type="button" className="btn" disabled={busy} onClick={() => void saveSession()}>{t('Save status and note')}</button>
          <span className="muted" aria-live="polite">{saved ? t('Saved.') : ''}</span>
        </div>
      </section>

      <div className="toolbar">
        <Link className="btn secondary" to={`/plan/now/${session.id}?learner=${encodeURIComponent(learnerId)}`}>{t('Show Now and Next to the learner')}</Link>
      </div>
      <Evidence learnerId={learnerId} subjectType="SESSION" subjectId={session.id} />
      <Comments learnerId={learnerId} subjectType="SESSION" subjectId={session.id} onChange={() => void onChanged()} />
    </PlannerDialog>
  );
}

function ItemPanel({ learnerId, item, measure, programId, instructions, onSaved }: { learnerId: string; item: Item; measure: Measure | null; programId: string | null; instructions: string | null; onSaved: () => Promise<void> | void }) {
  const t = useT();
  const id = useId();
  const r = item.result ?? {};
  const [status, setStatus] = useState<ItemStatus>(item.status);
  const [correct, setCorrect] = useState(v(r.correct));
  const [total, setTotal] = useState(v(r.total));
  const [prompt, setPrompt] = useState(r.prompt ?? '');
  const [stepsDone, setStepsDone] = useState(v(r.stepsDone));
  const [stepsTotal, setStepsTotal] = useState(v(r.stepsTotal));
  const [count, setCount] = useState(v(r.count));
  const [mins, setMins] = useState(r.seconds != null ? String(Math.round(r.seconds / 60)) : '');
  const [rating, setRating] = useState<number | undefined>(r.rating);
  const [text, setText] = useState(r.text ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const recordable = item.kind === 'ACTIVITY' && !programId && measure;
  const touch = <T,>(set: (x: T) => void) => (x: T) => { set(x); setSaved(false); };

  function result(): Result | null {
    if (item.kind === 'TEXT') return text.trim() ? { text: text.trim() } : null;
    if (!recordable) return item.result;
    const base: Result = text.trim() ? { text: text.trim() } : {};
    switch (measure) {
      case 'TRIALS': return { ...base, correct: n(correct), total: n(total), prompt: prompt || undefined };
      case 'STEPS': return { ...base, stepsDone: n(stepsDone), stepsTotal: n(stepsTotal) };
      case 'COUNT': return { ...base, count: n(count) };
      case 'DURATION': return { ...base, seconds: n(mins) != null ? n(mins)! * 60 : undefined };
      case 'RATING': return { ...base, rating };
      default: return text.trim() ? base : null;
    }
  }

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await saveResult(learnerId, item.id, status, result());
      await onSaved();
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  const num = (key: string, label: string, value: string, set: (x: string) => void, max = 999) => (
    <div className="field">
      <label htmlFor={`${id}-${key}`}>{label}</label>
      <input id={`${id}-${key}`} type="number" inputMode="numeric" min={0} max={max} value={value} onChange={(e) => touch(set)(e.target.value)} />
    </div>
  );

  return (
    <div className="planner-item-panel">
      <div className="planner-edit-item-head">
        <strong>{itemLabel(item)}</strong>
        <span className="pill">{t(ITEM_STATUS_LABEL[item.status])}</span>
      </div>
      {item.targetStatement && <p className="planner-meta">{t('Target: {target}', { target: item.targetStatement })}</p>}
      <ul className="planner-item-facts">
        {item.targetTrials != null && <li>{t('Target answers: {n}', { n: item.targetTrials })}</li>}
        {item.minutes != null && <li>{t('{n} minutes', { n: item.minutes })}</li>}
        {item.materials && <li>{t('Materials: {what}', { what: item.materials })}</li>}
        {item.promptStart && <li>{t('Starting help: {level}', { level: t(item.promptStart) })}</li>}
        {item.reward && <li>{t('Reward: {what}', { what: item.reward })}</li>}
      </ul>
      {instructions && <p className="planner-instructions">{instructions}</p>}

      {programId && (
        <div className="planner-item-program">
          <Link className="btn" to={`/play/${programId}?learner=${encodeURIComponent(learnerId)}`}>{t('Play')}</Link>
          <span>
            {item.measured && item.measured.total > 0
              ? t('Score from the app: {correct} of {total} right first time', { correct: item.measured.correct, total: item.measured.total })
              : t('No score yet. It fills in by itself when this is played.')}
          </span>
        </div>
      )}

      {recordable && (
        <div className="planner-result">
          <p className="field-hint">{t('What to record: {measure}', { measure: t(MEASURE_LABEL[measure]) })}</p>
          {measure === 'TRIALS' && (
            <div className="row">
              {num('c', t('Right answers'), correct, setCorrect)}
              {num('t', t('Tries in total'), total, setTotal)}
              <div className="field">
                <label htmlFor={`${id}-p`}>{t('Help given')}</label>
                <select id={`${id}-p`} value={prompt} onChange={(e) => touch(setPrompt)(e.target.value)}>
                  <option value="">{t('Not set')}</option>
                  {PROMPT_LEVELS.map((p) => <option key={p} value={p}>{t(p)}</option>)}
                </select>
              </div>
            </div>
          )}
          {measure === 'STEPS' && (
            <div className="row">
              {num('sd', t('Steps done'), stepsDone, setStepsDone)}
              {num('st', t('Steps in total'), stepsTotal, setStepsTotal)}
            </div>
          )}
          {measure === 'COUNT' && <div className="row">{num('n', t('How many times'), count, setCount, 99999)}</div>}
          {measure === 'DURATION' && <div className="row">{num('m', t('Minutes'), mins, setMins, 1440)}</div>}
          {measure === 'RATING' && (
            <fieldset className="planner-fieldset">
              <legend>{t('Rating from 1 to 5')}</legend>
              <div className="planner-radios">
                {[1, 2, 3, 4, 5].map((x) => (
                  <label key={x}><input type="radio" name={`${id}-r`} checked={rating === x} onChange={() => touch(setRating)(x)} /> {x}</label>
                ))}
              </div>
            </fieldset>
          )}
        </div>
      )}

      <fieldset className="planner-fieldset">
        <legend>{t('Status')}</legend>
        <div className="planner-radios">
          {(Object.keys(ITEM_STATUS_LABEL) as ItemStatus[]).map((s) => (
            <label key={s}>
              <input type="radio" name={`${id}-s`} checked={status === s} onChange={() => touch(setStatus)(s)} /> {t(ITEM_STATUS_LABEL[s])}
            </label>
          ))}
        </div>
      </fieldset>

      {(item.kind === 'TEXT' || (recordable && measure !== 'NOTES')) && (
        <div className="field">
          <label htmlFor={`${id}-txt`}>{t('Note (optional)')}</label>
          <input id={`${id}-txt`} maxLength={2000} value={text} onChange={(e) => touch(setText)(e.target.value)} />
        </div>
      )}
      {recordable && measure === 'NOTES' && (
        <div className="field">
          <label htmlFor={`${id}-txt`}>{t('Notes')}</label>
          <textarea id={`${id}-txt`} rows={3} maxLength={4000} value={text} onChange={(e) => touch(setText)(e.target.value)} />
        </div>
      )}

      {error && <div className="error" role="alert">{error}</div>}
      <div className="toolbar">
        <button type="button" className="btn secondary" disabled={busy} onClick={() => void save()}>{t('Save this item')}</button>
        <span className="muted" aria-live="polite">{saved ? t('Saved.') : ''}</span>
      </div>
    </div>
  );
}
